export type AiProviderId = 'bedrock' | 'openai'

export type AiSettings = {
  provider: AiProviderId
  bedrockModelId: string
  openaiModelId: string
}

export const DEFAULT_AI_PROVIDER: AiProviderId = 'openai'
export const DEFAULT_OPENAI_MODEL_ID = 'gpt-6-luna'
// Cost-conscious default for interactive AI (copilot/analysis chat); correctness-critical server
// workflows use the explicit Terra tier instead.
export const DEFAULT_AI_REASONING_EFFORT = 'low'

// Interactive clients send both provider and model explicitly. This keeps browser-side requests on
// OpenAI even if another provider credential is present in the backend environment.
export const COPILOT_MODEL_ID = DEFAULT_OPENAI_MODEL_ID
export const copilotProviderForAiRequest = (): AiProviderId => 'openai'
export const copilotModelId = (): string => COPILOT_MODEL_ID

const LS_PROVIDER_KEY = 'fusegrid_ai_provider'
const LS_MODEL_BEDROCK_KEY = 'fusegrid_ai_model_id_bedrock'
const LS_MODEL_OPENAI_KEY = 'fusegrid_ai_model_id_openai'
const LS_MODEL_LEGACY_KEY = 'fusegrid_ai_model_id'

const readLocal = (key: string): string => {
  if (typeof window === 'undefined') return ''
  try { return String(window.localStorage.getItem(key) || '') } catch { return '' }
}

const writeLocal = (key: string, value: string) => {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(key, String(value ?? '')) } catch { }
}

export const normalizeProvider = (value?: string | null): AiProviderId => {
  const v = String(value || '').trim().toLowerCase()
  if (v === 'openai') return 'openai'
  // For now all interactive AI requests are pinned to OpenAI so tests and
  // optimization are against one configured model path.
  if (v === 'bedrock') return DEFAULT_AI_PROVIDER
  return DEFAULT_AI_PROVIDER
}

export const readAiSettings = (): AiSettings => {
  const provider = DEFAULT_AI_PROVIDER
  const legacy = readLocal(LS_MODEL_LEGACY_KEY).trim()
  const bedrockModelId = (readLocal(LS_MODEL_BEDROCK_KEY).trim() || legacy).trim()
  // Keep interactive AI requests pinned to one OpenAI model path.
  const openaiModelId = DEFAULT_OPENAI_MODEL_ID
  return { provider, bedrockModelId, openaiModelId }
}

export const writeAiProvider = (provider: AiProviderId) => {
  writeLocal(LS_PROVIDER_KEY, provider)
}

export const writeAiModelId = (provider: AiProviderId, modelId: string) => {
  const value = String(modelId || '').trim()
  if (provider === 'openai') {
    writeLocal(LS_MODEL_OPENAI_KEY, value)
    return
  }
  writeLocal(LS_MODEL_BEDROCK_KEY, value)
  // Legacy key for backward compatibility with older builds.
  writeLocal(LS_MODEL_LEGACY_KEY, value)
}

export const getModelIdForProvider = (settings: AiSettings, provider?: AiProviderId | null): string => {
  const p = provider ? provider : settings.provider
  return p === 'openai' ? settings.openaiModelId : settings.bedrockModelId
}

export const providerForAiRequest = (provider?: AiProviderId | null): AiProviderId => {
  return normalizeProvider(provider)
}
